/**
 * Players Browser - engine-wired pool with locked tier + rank + ADP.
 *
 * [Wire-to-engine 2026-07-31] Backend `/api/players` now LEFT JOINs
 * pgf_axis_scores + player_metrics, returns positional_rank, tier_label,
 * composite_score, ADP + PPG per scoring_format, and the fact_packet
 * transparency flags. Sort is authoritative on positional_rank (backend
 * ORDER BY positional_rank ASC NULLS LAST, avg_adp ASC NULLS LAST) so
 * the client never re-sorts on projected_points.
 *
 * Columns: TIER | PGF# | Player | Pos | Team | Bye | ADP# | POINTS
 * The POINTS column reads PPG for the selected scoring format or the
 * baked projected_points depending on the toggle.
 *
 * Product gate: Find / Fade badges render behind `user.role === admin`
 * ONLY. Composite trust check still pending before public rollout.
 */
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useTheme } from "../contexts/ThemeContext";
import { authHeaders, useAuth } from "../contexts/AuthContext";
import SeasonShell from "../components/season/SeasonShell";
import { PlayerAvatar } from "../components/PlayerHeadshot";
import DiamondCard from "../components/dashboard/DiamondCard";
import EdgeListsSection from "../components/dashboard/EdgeListsSection";
import PlayerAnalysisCanvas from "../components/analytics/PlayerAnalysisCanvas";
import { X, TrendingUp, TrendingDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POS_FILTERS = ["ALL", "QB", "RB", "WR", "TE", "K", "DEF"];
const SCORING_FORMATS = [
  { key: "half_ppr", label: "0.5 PPR" },
  { key: "full_ppr", label: "Full PPR" },
  { key: "standard", label: "Standard" },
];
const POINTS_MODES = [
  { key: "ppg", label: "PPG" },
  { key: "proj", label: "Projected" },
];
const TIER_FILTERS = [
  "ALL", "elite", "top_tier", "solid", "roster_worthy",
  "bench", "deep", "insufficient_data",
];
// Tier badge palette. Kept neutral - no source names, no em dashes.
const TIER_BADGE = {
  elite:              { bg: "bg-[#FF6B00]/20", fg: "text-[#FF6B00]", label: "ELITE" },
  top_tier:           { bg: "bg-[#FF9F45]/20", fg: "text-[#FF9F45]", label: "TOP" },
  solid:              { bg: "bg-[#00C853]/20", fg: "text-[#00C853]", label: "SOLID" },
  roster_worthy:      { bg: "bg-[#4FC3F7]/20", fg: "text-[#4FC3F7]", label: "ROSTER" },
  bench:              { bg: "bg-[#9E9E9E]/20", fg: "text-[#B0B0B0]", label: "BENCH" },
  deep:               { bg: "bg-[#616161]/20", fg: "text-[#8E8E8E]", label: "DEEP" },
  insufficient_data:  { bg: "bg-[#2A2A2A]",    fg: "text-[#707070]", label: "NO DATA" },
  hard_status_out:    { bg: "bg-red-500/20",   fg: "text-red-400",   label: "OUT" },
};
const HARD_STATUS_LABEL = {
  RETIRED: "RET", SEASON_IR: "IR", SUSPENDED_SEASON: "SUS", PUP_SEASON: "PUP",
  OUT_SEASON: "OUT", NOT_ON_ROSTER: "FA",
};
// Slice caps: ALL tab keeps a soft cap, per-position tabs uncapped so
// deep WR / RB pools render in full.
const CAP_ALL = 300;
const CAP_POS = 500;


export default function PlayersBrowser() {
  const { id: leagueId } = useParams();
  const isAgnostic = !leagueId;
  const { isDark } = useTheme();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [players, setPlayers] = useState([]);
  const [pos, setPos] = useState("ALL");
  const [q, setQ] = useState("");
  const [scoringFormat, setScoringFormat] = useState("half_ppr");
  const [pointsMode, setPointsMode] = useState("ppg");
  const [tierFilter, setTierFilter] = useState("ALL");
  const [teamFilter, setTeamFilter] = useState("ALL");
  const [byeFilter, setByeFilter] = useState("ALL");
  const [hideInjured, setHideInjured] = useState(false);
  const [rookiesOnly, setRookiesOnly] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canvasData, setCanvasData] = useState(null);
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [canvasError, setCanvasError] = useState(null);

  // Refetch on scoring format change so ADP + PPG columns follow the
  // format. Rank + tier are byte-identical across recomputes; they
  // just re-source from pgf_axis_scores for the new format.
  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setLoading(true);
    axios
      .get(`${API}/players`, {
        headers: authHeaders(),
        params: { season_year: 2026, scoring_format: scoringFormat, limit: 2500 },
      })
      .then((r) => {
        if (cancelled) return;
        setPlayers(Array.isArray(r.data) ? r.data : r.data?.players || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e?.response?.data?.detail || e?.message || "Failed to load players");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scoringFormat]);

  useEffect(() => {
    if (!selectedPlayerId) {
      setCanvasData(null); setCanvasError(null); return;
    }
    let cancelled = false;
    setCanvasLoading(true);
    setCanvasError(null);
    (async () => {
      try {
        const params = { season_year: 2025, scoring_format: "half_ppr" };
        if (leagueId) params.league_id = leagueId;
        const { data } = await axios.get(
          `${API}/analytics/players/${selectedPlayerId}/canvas`,
          { headers: authHeaders(), params },
        );
        if (!cancelled) setCanvasData(data);
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail;
          setCanvasError(
            status === 422 && typeof detail === "string" && detail.includes("does not score")
              ? "Analytics unavailable for this position."
              : status === 404
                ? "No pre-computed analytics on file for this player yet."
                : "Analytics temporarily unavailable.",
          );
          setCanvasData(null);
        }
      } finally {
        if (!cancelled) setCanvasLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPlayerId, leagueId]);

  const selectedPlayer = useMemo(
    () => (selectedPlayerId ? players.find((p) => p.id === selectedPlayerId) : null),
    [selectedPlayerId, players],
  );

  // Team + bye dropdowns are derived from the current pool so a
  // WR-only view only offers teams that have WRs.
  const teamOptions = useMemo(() => {
    const s = new Set(players.filter((p) => pos === "ALL" || p.position === pos)
      .map((p) => p.nfl_team).filter(Boolean));
    return ["ALL", ...Array.from(s).sort()];
  }, [players, pos]);
  const byeOptions = useMemo(() => {
    const s = new Set(players.filter((p) => pos === "ALL" || p.position === pos)
      .map((p) => p.bye_week).filter((b) => b != null));
    return ["ALL", ...Array.from(s).sort((a, b) => a - b)];
  }, [players, pos]);

  const filtered = useMemo(() => {
    return players
      .filter((p) => pos === "ALL" || p.position === pos)
      .filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()))
      .filter((p) => tierFilter === "ALL" || p.tier_label === tierFilter)
      .filter((p) => teamFilter === "ALL" || p.nfl_team === teamFilter)
      .filter((p) => byeFilter === "ALL" || p.bye_week === byeFilter)
      .filter((p) => !hideInjured || !p.hard_status_tag)
      .filter((p) => !rookiesOnly || (p.years_exp === 0 || p.years_exp == null));
    // Sort is authoritative from the backend - do NOT re-sort here.
  }, [players, pos, q, tierFilter, teamFilter, byeFilter, hideInjured, rookiesOnly]);

  const cap = pos === "ALL" ? CAP_ALL : CAP_POS;

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  const body = (
    <>
      <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mb-4">Players</h1>

      {isAgnostic && (
        <>
          <div className="mb-6">
            <DiamondCard isDark={isDark} />
          </div>
          <div className="mb-6">
            <EdgeListsSection limit={8} isDark={isDark} />
          </div>
        </>
      )}

      {loadError && (
        <div
          className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 text-sm flex items-center justify-between"
          data-testid="players-load-error"
        >
          <span>{loadError}</span>
          <button
            onClick={() => window.location.reload()}
            className="px-3 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-xs font-display font-bold uppercase tracking-wide"
            data-testid="players-retry"
          >Retry</button>
        </div>
      )}

      {/* Filter bar (row 1): search + position tabs. */}
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search players..."
          className={`flex-1 px-4 py-2 rounded-lg ${isDark ? "bg-[#252525] text-white" : "bg-white text-black"} border ${cardBorder}`}
          data-testid="players-search"
        />
        <div className="flex gap-1 overflow-x-auto">
          {POS_FILTERS.map((p) => (
            <button
              key={p}
              onClick={() => setPos(p)}
              className={`px-3 py-2 rounded-lg text-xs font-display font-bold uppercase tracking-wide transition ${
                pos === p
                  ? "bg-[#FF6B00]/15 text-[#FF6B00] ring-1 ring-[#FF6B00]/40"
                  : `${muted} hover:${fg} border ${cardBorder}`
              }`}
              data-testid={`players-filter-${p}`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Filter bar (row 2): scoring format + points mode + tier +
          team + bye + hide-injured + rookies-only. */}
      <div className="flex flex-wrap gap-2 items-center mb-4 text-xs">
        <SegmentedControl
          label="Format"
          options={SCORING_FORMATS}
          value={scoringFormat}
          onChange={setScoringFormat}
          testidPrefix="fmt"
        />
        <SegmentedControl
          label="Points"
          options={POINTS_MODES}
          value={pointsMode}
          onChange={setPointsMode}
          testidPrefix="pts"
        />
        <DropdownFilter
          label="Tier"
          options={TIER_FILTERS.map((t) => ({ key: t, label: t === "ALL" ? "All" : (TIER_BADGE[t]?.label || t) }))}
          value={tierFilter}
          onChange={setTierFilter}
          testid="players-tier-filter"
        />
        <DropdownFilter
          label="Team"
          options={teamOptions.map((t) => ({ key: t, label: t === "ALL" ? "All" : t }))}
          value={teamFilter}
          onChange={setTeamFilter}
          testid="players-team-filter"
        />
        <DropdownFilter
          label="Bye"
          options={byeOptions.map((b) => ({ key: b, label: b === "ALL" ? "All" : `Wk ${b}` }))}
          value={byeFilter}
          onChange={setByeFilter}
          testid="players-bye-filter"
        />
        <label className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border ${cardBorder} cursor-pointer`}>
          <input
            type="checkbox"
            checked={hideInjured}
            onChange={(e) => setHideInjured(e.target.checked)}
            data-testid="players-hide-injured"
          />
          <span className="uppercase tracking-widest font-display font-bold">Hide Injured</span>
        </label>
        <label className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border ${cardBorder} cursor-pointer`}>
          <input
            type="checkbox"
            checked={rookiesOnly}
            onChange={(e) => setRookiesOnly(e.target.checked)}
            data-testid="players-rookies-only"
          />
          <span className="uppercase tracking-widest font-display font-bold">Rookies Only</span>
        </label>
      </div>

      <div className={`rounded-2xl ${cardBg} border ${cardBorder} overflow-hidden`} data-testid="players-table">
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className={`sticky top-0 ${cardBg} text-[10px] uppercase tracking-widest ${muted}`}>
              <tr>
                <th className="px-3 py-3 text-left">Tier</th>
                <th className="px-3 py-3 text-right">PGF</th>
                <th className="px-3 py-3 text-left">Player</th>
                <th className="px-3 py-3 text-left">Pos</th>
                <th className="px-3 py-3 text-left">Team</th>
                <th className="px-3 py-3 text-right">Bye</th>
                <th className="px-3 py-3 text-right">ADP</th>
                <th className="px-3 py-3 text-right">
                  {pointsMode === "ppg" ? (
                    <>
                      PPG
                      <div className="text-[8px] normal-case tracking-normal opacity-60">
                        prior yr (2025)
                      </div>
                    </>
                  ) : "PROJ"}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className={`px-3 py-8 text-center ${muted}`} data-testid="players-loading">Loading pool...</td></tr>
              )}
              {!loading && filtered.slice(0, cap).map((p) => (
                <PlayerRow
                  key={p.id}
                  p={p}
                  isDark={isDark}
                  muted={muted}
                  pointsMode={pointsMode}
                  isAdmin={isAdmin}
                  onOpen={() => setSelectedPlayerId(p.id)}
                />
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className={`px-3 py-8 text-center ${muted}`} data-testid="players-empty">No players match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className={`text-[10px] ${muted} mt-2`}>
        Showing {Math.min(cap, filtered.length)} of {filtered.length}
        {isAdmin && (
          <span className="ml-2 text-[#FF6B00]" data-testid="players-admin-flag">
            [admin: Find/Fade badges visible]
          </span>
        )}
      </p>

      {selectedPlayerId && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-20 p-4"
          onClick={() => setSelectedPlayerId(null)}
          data-testid="players-analytics-modal"
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-[#181818] border border-[#2A2A2A] overflow-hidden flex flex-col max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2A]">
              <div className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-bold">
                Player Analytics
              </div>
              <button
                type="button"
                onClick={() => setSelectedPlayerId(null)}
                className="text-[#A0A0A0] hover:text-white"
                data-testid="players-analytics-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <PlayerAnalysisCanvas
                player={canvasData?.player || selectedPlayer}
                pgfCanvas={canvasData}
                loading={canvasLoading}
                error={canvasError}
                data-testid="players-browser-canvas"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (isAgnostic) {
    return (
      <div className="min-h-screen bg-[#111111] text-[#F5F5F5]">
        <div className="max-w-6xl mx-auto px-4 py-6">{body}</div>
      </div>
    );
  }
  return <SeasonShell>{body}</SeasonShell>;
}


// ─── Row + shared UI atoms ────────────────────────────────────────────


function PlayerRow({ p, isDark, muted, pointsMode, isAdmin, onOpen }) {
  const tier = p.tier_label ? TIER_BADGE[p.tier_label] : null;
  const pts = pointsMode === "ppg" ? p.pts_per_game : p.projected_points;
  const points = pts != null ? (pointsMode === "ppg" ? pts.toFixed(1) : Math.round(pts)) : "-";
  const hardTag = p.hard_status_tag ? HARD_STATUS_LABEL[p.hard_status_tag] || p.hard_status_tag : null;
  const isRookie = p.years_exp === 0;
  return (
    <tr
      className="hover:bg-[#FF6B00]/5 cursor-pointer border-t border-[#2A2A2A]/40"
      data-testid={`players-row-${p.id}`}
      onClick={onOpen}
    >
      <td className="px-3 py-2">
        {tier ? (
          <span
            className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-display font-black tracking-widest ${tier.bg} ${tier.fg}`}
            data-testid={`players-tier-badge-${p.id}`}
          >
            {tier.label}
          </span>
        ) : (
          <span className={`text-[9px] ${muted}`}>-</span>
        )}
      </td>
      <td className="px-3 py-2 text-right font-display font-black text-xs">
        {p.pgf_rank_label ? (
          <span data-testid={`players-pgf-rank-${p.id}`}>{p.pgf_rank_label}</span>
        ) : (
          <span className={muted}>-</span>
        )}
      </td>
      <td className="px-3 py-2 flex items-center gap-2">
        <PlayersBrowserRowThumb photoUrl={p.photo_url} playerId={p.id} position={p.position} />
        <span className="font-display font-bold uppercase text-xs tracking-wide truncate">{p.name}</span>
        <InlineBadges
          hardTag={hardTag}
          isRookie={isRookie}
          unvalidatedCapital={p.unvalidated_capital_position}
          edgeLabel={p.edge_label}
          findFadeLabel={p.find_fade_label}
          isAdmin={isAdmin}
        />
      </td>
      <td className="px-3 py-2 text-xs">{p.position}</td>
      <td className={`px-3 py-2 text-xs ${muted}`}>{p.nfl_team}</td>
      <td className={`px-3 py-2 text-xs text-right ${muted}`}>{p.bye_week || "-"}</td>
      <td className={`px-3 py-2 text-xs text-right ${muted}`} data-testid={`players-adp-${p.id}`}>
        {p.adp_rank ? `${p.position}${p.adp_rank}` : "-"}
      </td>
      <td className="px-3 py-2 text-right font-display font-black" data-testid={`players-points-${p.id}`}>
        {points}
      </td>
    </tr>
  );
}


function InlineBadges({ hardTag, isRookie, unvalidatedCapital, edgeLabel, findFadeLabel, isAdmin }) {
  return (
    <span className="flex items-center gap-1 shrink-0">
      {hardTag && (
        <span
          className="px-1.5 py-0.5 rounded text-[9px] font-display font-black bg-red-500/20 text-red-400 tracking-widest"
          data-testid={`players-hard-status-${hardTag}`}
        >
          {hardTag}
        </span>
      )}
      {isRookie && (
        <span
          className="px-1.5 py-0.5 rounded text-[9px] font-display font-black bg-[#FFD54F]/20 text-[#FFD54F] tracking-widest"
          data-testid="players-rookie-chip"
        >
          RK
        </span>
      )}
      {unvalidatedCapital && (
        <span
          className="px-1.5 py-0.5 rounded text-[9px] font-display font-black bg-[#7E57C2]/20 text-[#B39DDB] tracking-widest"
          title="Rookie draft-capital prior applied; primary inputs not fully validated"
          data-testid="players-capital-chip"
        >
          CAP
        </span>
      )}
      {/* Find / Fade badge: INTERNAL only. Product gate holds until
          composite trust check passes. Render admin-only for now. */}
      {isAdmin && findFadeLabel === "find" && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-display font-black bg-[#00C853]/20 text-[#00C853] tracking-widest"
          title="Find - PGF above market + market drafting late (admin preview)"
          data-testid="players-find-badge"
        >
          <TrendingUp size={9} /> FIND
        </span>
      )}
      {isAdmin && findFadeLabel === "fade" && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-display font-black bg-[#FF6B00]/20 text-[#FF6B00] tracking-widest"
          title="Fade - PGF below market + market drafting early (admin preview)"
          data-testid="players-fade-badge"
        >
          <TrendingDown size={9} /> FADE
        </span>
      )}
      {isAdmin && edgeLabel && !findFadeLabel && (
        <span
          className="px-1.5 py-0.5 rounded text-[9px] font-display font-black bg-[#4FC3F7]/15 text-[#4FC3F7] tracking-widest"
          title={`Edge ${edgeLabel}, no ADP cost-gate (admin preview)`}
          data-testid="players-edge-badge"
        >
          EDGE {edgeLabel === "up" ? "+" : "-"}
        </span>
      )}
    </span>
  );
}


function SegmentedControl({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-widest text-[#808080] font-display font-bold">{label}</span>
      <div className="flex rounded-lg overflow-hidden border border-[#2A2A2A]" data-testid={`${testidPrefix}-group`}>
        {options.map((o) => (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className={`px-2 py-1 text-[10px] font-display font-bold uppercase tracking-widest transition ${
              value === o.key
                ? "bg-[#FF6B00]/20 text-[#FF6B00]"
                : "bg-transparent text-[#A0A0A0] hover:text-white"
            }`}
            data-testid={`${testidPrefix}-${o.key}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}


function DropdownFilter({ label, options, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-widest text-[#808080] font-display font-bold">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value === "ALL" ? "ALL" : (isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)))}
        className="px-2 py-1 rounded-lg bg-[#1C1C1C] border border-[#2A2A2A] text-[10px] uppercase tracking-widest font-display font-bold text-[#F5F5F5]"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={String(o.key)} value={o.key}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}


function PlayersBrowserRowThumb({ photoUrl, playerId, position }) {
  const [errored, setErrored] = useState(false);
  if (!photoUrl || errored) {
    return (
      <div
        className="w-7 h-7 rounded-full overflow-hidden shrink-0 border border-[#333] bg-[#252525] flex items-center justify-center"
        data-testid={`players-row-thumb-${playerId}`}
      >
        <div style={{ transform: "scale(0.636)", transformOrigin: "center" }}>
          <PlayerAvatar playerId={playerId} position={position} size="sm" />
        </div>
      </div>
    );
  }
  return (
    <img
      src={photoUrl}
      alt={position || "Player"}
      loading="lazy"
      onError={() => setErrored(true)}
      className="w-7 h-7 rounded-full object-cover border border-[#333] bg-[#252525] shrink-0"
      data-testid={`players-row-thumb-${playerId}`}
    />
  );
}
